import { Badge, Button, Group, Loader, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityFoodRO } from '@routellers/types/dist/city-food/city-food.ro';
import {
    GUIDE_STATUS_ORDER,
    GuideStatusEnum,
} from '@routellers/types/dist/city-guide/enum/guide-status.enum';
import { CONTENT_BASE_LANGUAGE } from '@routellers/types/dist/shared/content-base-language';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { CityQuickSelect, parseCityFilter } from '../components/city-quick-select';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { FoodEditor } from '../editors/food-editor';
import { enumOptions } from '../i18n';
import { LIST_PAGE_SIZE, totalFromMeta } from '../utils/list';

const ALL = 'all';

const statusColor: Record<GuideStatusEnum, string> = {
    [GuideStatusEnum.DRAFT]: 'gray',
    [GuideStatusEnum.PUBLISHED]: 'green',
};

const displayName = (row: CityFoodRO) =>
    row.locales?.find((locale) => locale.language === CONTENT_BASE_LANGUAGE && locale.name)?.name ||
    row.title;

export const FoodsPage = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<CityFoodRO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [cityFilter, setCityFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState(ALL);
    const [editorId, setEditorId] = useState<number | null>(null);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);
        const cityId = parseCityFilter(cityFilter);

        try {
            const where: Record<string, unknown> = {};

            if (cityId) where.cityId = cityId;
            if (statusFilter !== ALL) where.status = statusFilter;
            if (!Object.keys(where).length) where.id = null;

            const { data } = await api.cityFoodsApi.query({
                page,
                limit: LIST_PAGE_SIZE,
                searchText: search.trim() || undefined,
                where,
            });

            setRows(data?.items || []);
            setTotal(totalFromMeta(data?.meta, data?.items?.length || 0));
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
    }, [cityFilter, statusFilter]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityFilter, statusFilter, page]);

    const statusOptions = enumOptions(GUIDE_STATUS_ORDER, t, 'enums.guideStatus');

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.foods.title')}
                subtitle={t('pages.foods.subtitle')}
                action={
                    <Button
                        onClick={() => {
                            setEditorId(null);
                            setCreating(true);
                        }}
                    >
                        {t('pages.foods.create')}
                    </Button>
                }
            />

            <Group gap='sm' wrap='wrap'>
                <TextInput
                    placeholder={t('pages.foods.searchPlaceholder')}
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    onKeyDown={(event) => {
                        if (event.key !== 'Enter') return;
                        if (page !== 1) setPage(1);
                        else load();
                    }}
                    w={260}
                />
                <CityQuickSelect
                    value={cityFilter}
                    onChange={(value) => {
                        setPage(1);
                        setCityFilter(value);
                    }}
                />
                <Select
                    w={180}
                    data={[{ value: ALL, label: t('pages.foods.statusAll') }, ...statusOptions]}
                    value={statusFilter}
                    onChange={(value) => setStatusFilter(value || ALL)}
                />
            </Group>

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={720}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('pages.foods.columns.name')}</Table.Th>
                                    <Table.Th>{t('pages.foods.columns.city')}</Table.Th>
                                    <Table.Th>{t('pages.foods.columns.district')}</Table.Th>
                                    <Table.Th>{t('pages.foods.columns.places')}</Table.Th>
                                    <Table.Th>{t('pages.foods.columns.status')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {rows.map((row) => (
                                    <Table.Tr
                                        key={row.id}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            setCreating(false);
                                            setEditorId(row.id);
                                        }}
                                    >
                                        <Table.Td>
                                            <Text fw={550}>{displayName(row)}</Text>
                                            <Text size='xs' c='dimmed'>
                                                {row.slug}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>{row.cityTitle || `#${row.cityId}`}</Table.Td>
                                        <Table.Td>{row.districtTitle || '—'}</Table.Td>
                                        <Table.Td>{row.places?.length || 0}</Table.Td>
                                        <Table.Td>
                                            <Badge color={statusColor[row.status]} variant='light'>
                                                {t(`enums.guideStatus.${row.status}`)}
                                            </Badge>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                                {!rows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={5}>
                                            <Text c='dimmed'>{t('pages.foods.empty')}</Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <ListPagination page={page} totalItems={total} onChange={setPage} />
                </>
            )}

            <FoodEditor
                opened={creating || editorId != null}
                foodId={creating ? null : editorId}
                defaultCityId={parseCityFilter(cityFilter)}
                onClose={() => {
                    setCreating(false);
                    setEditorId(null);
                }}
                onSaved={load}
                onCreated={(id) => {
                    setCreating(false);
                    setEditorId(id);
                }}
                onDeleted={() => {
                    setCreating(false);
                    setEditorId(null);
                    load();
                }}
            />
        </Stack>
    );
};
